export type PortalCountry = 'SE' | 'FI';

export type GuideVariant =
  | 'internal-SE'
  | 'external-SE'
  | 'internal-FI'
  | 'external-FI';

export type FileAsset = {
  fileName: string;
  url: string;
  thumbnailUrl: string;
  title: string;
};

export interface GuideVariantConfig {
  label: string;
  folder: GuideVariant;
  isInternal: boolean;
  country: PortalCountry;
}

export interface GuideDocument {
  path: string;
  variant: GuideVariant;
  title: string;
}

export interface GuideSection {
  key: GuideVariant;
  label: string;
  country: PortalCountry;
  isInternal: boolean;
  guides: GuideDocument[];
}

export interface GuideSectionVisibilityContext {
  sections: GuideSection[];
  searchTerm: string;
  isInternalUser: boolean;
  portalCountry: PortalCountry;
}

export const GUIDE_VARIANTS: Record<GuideVariant, GuideVariantConfig> = {
  'internal-SE': {
    label: 'Internal SE',
    folder: 'internal-SE',
    isInternal: true,
    country: 'SE',
  },
  'external-SE': {
    label: 'External SE',
    folder: 'external-SE',
    isInternal: false,
    country: 'SE',
  },
  'internal-FI': {
    label: 'Internal FI',
    folder: 'internal-FI',
    isInternal: true,
    country: 'FI',
  },
  'external-FI': {
    label: 'External FI',
    folder: 'external-FI',
    isInternal: false,
    country: 'FI',
  },
};

export const GUIDE_INTERNAL_SE: GuideDocument[] = [
  {
    path: 'Internal_DeliveryPortal_SWE_Construction Documents.pdf',
    variant: 'internal-SE',
    title: 'Construction Documents',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Installation Reporting with updated Preinstallation.pdf',
    variant: 'internal-SE',
    title: 'Installation Reporting with updated Preinstallation',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Reporting in Rollout.pdf',
    variant: 'internal-SE',
    title: 'Reporting in Rollout',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Rollout Progress.pdf',
    variant: 'internal-SE',
    title: 'Rollout Progress',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Sharepoint Export.pdf',
    variant: 'internal-SE',
    title: 'Sharepoint Export',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Span Protection.pdf',
    variant: 'internal-SE',
    title: 'Span Protection',
  },
  {
    path: 'Internal_DeliveryPortal_SWE_Span Reporting.pdf',
    variant: 'internal-SE',
    title: 'Span Reporting',
  },
];

export const GUIDE_EXTERNAL_SE: GuideDocument[] = [
  {
    path: 'External_DeliveryPortal_SWE_Construction Documents.pdf',
    variant: 'external-SE',
    title: 'Construction Documents',
  },
  {
    path: 'External_DeliveryPortal_SWE_Installation Reporting.pdf',
    variant: 'external-SE',
    title: 'Installation Reporting',
  },
  {
    path: 'External_DeliveryPortal_SWE_Reporting.pdf',
    variant: 'external-SE',
    title: 'Reporting',
  },
  {
    path: 'External_DeliveryPortal_SWE_Rollout Progress.pdf',
    variant: 'external-SE',
    title: 'Rollout Progress',
  },
  {
    path: 'External_DeliveryPortal_SWE_Span Reporting.pdf',
    variant: 'external-SE',
    title: 'Span Reporting',
  },
];

export const GUIDE_INTERNAL_FI: GuideDocument[] = [
  {
    path: 'Internal_DeliveryPortal_FIN_Construction Documents.pdf',
    variant: 'internal-FI',
    title: 'Construction Documents',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Installation Reporting_Updated Preinstallation.pdf',
    variant: 'internal-FI',
    title: 'Installation Reporting Updated Preinstallation',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Installation Reporting.pdf',
    variant: 'internal-FI',
    title: 'Installation Reporting',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Land Survey.pdf',
    variant: 'internal-FI',
    title: 'Land Survey',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Network Import.pdf',
    variant: 'internal-FI',
    title: 'Network Import',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Reporting.pdf',
    variant: 'internal-FI',
    title: 'Reporting',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_SharePoint Export.pdf',
    variant: 'internal-FI',
    title: 'SharePoint Export',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Span Protection.pdf',
    variant: 'internal-FI',
    title: 'Span Protecting',
  },
  {
    path: 'Internal_DeliveryPortal_FIN_Span Reporting.pdf',
    variant: 'internal-FI',
    title: 'Span Reporting',
  },
];

export const GUIDE_EXTERNAL_FI: GuideDocument[] = [
  {
    path: 'External_DeliveryPortal_FIN_Construction Documents.pdf',
    variant: 'external-FI',
    title: 'Construction Documents',
  },
  {
    path: 'External_DeliveryPortal_FIN_Installation Reporting with Updated Preinstallation.pdf',
    variant: 'external-FI',
    title: 'Installation Reporting with Updated Preinstallation',
  },
  {
    path: 'External_DeliveryPortal_FIN_Installation Reporting.pdf',
    variant: 'external-FI',
    title: 'Installation Reporting',
  },
  {
    path: 'External_DeliveryPortal_FIN_Reporting.pdf',
    variant: 'external-FI',
    title: 'Reporting',
  },
  {
    path: 'External_DeliveryPortal_FIN_Rollout Progress.pdf',
    variant: 'external-FI',
    title: 'Rollout Progress',
  },
  {
    path: 'External_DeliveryPortal_FIN_Span Reporting.pdf',
    variant: 'external-FI',
    title: 'Span Reporting',
  },
  {
    path: 'Plot_design_signing_DeliveryPortal-FI.pdf',
    variant: 'external-FI',
    title: 'Plot design signing',
  },
  {
    path: 'Complete_installation_report-fi.pdf',
    variant: 'external-FI',
    title: 'Complete installation',
  },
];

export const GUIDES_BY_VARIANT: Record<GuideVariant, GuideDocument[]> = {
  'internal-SE': GUIDE_INTERNAL_SE,
  'external-SE': GUIDE_EXTERNAL_SE,
  'internal-FI': GUIDE_INTERNAL_FI,
  'external-FI': GUIDE_EXTERNAL_FI,
};
